/**
 * PR-H7 (ADR-0030) — `ANKETA_TO_FURNITURE_TYPE` identity mapping через
 * `ANKETA_TYPE_CARDS.label` lookup. Calculator entry labels = single source
 * of truth для Quote `furnitureType` value end-to-end (Calculator → Quote →
 * PDF). Никакого transformation/mapping anywhere.
 *
 * **Не дублируем `QUOTE_FURNITURE_TYPES`** — re-export канонического vocabulary
 * из `@pomoshnik/shared` для backward-compat consumer'ов (e.g. tests). Picker
 * (`FurnitureTypePicker.tsx`) iterates `ANKETA_TYPE_CARDS` напрямую вместо
 * этого re-export'а.
 *
 * **Granularity changes vs pre-PR-H7:** раньше 3 sanузел-анкеты сворачивались
 * в общий bucket `'Санузел'`; теперь — 3 distinct labels (`Санузел: тумба со
 * столешницей` / `Санузел: шкаф над антресолью` / `Санузел: шкаф`) per
 * Calculator entry vocabulary. Решение Aibek 2026-05-28 (ADR-0030 §4).
 *
 * References:
 *   - ADR-0030 §4 (identity mapping rationale)
 *   - ADR-0030 §2 (vocabulary 10→8 mirroring ANKETA_TYPE_CARDS)
 *   - PRD `docs/prd/v4/10-block-12-calculator.md` Экран 2.12.10
 */

import type { CalculatorAnketaType } from '@pomoshnik/shared';
import { QUOTE_FURNITURE_TYPES, type QuoteFurnitureType } from '@pomoshnik/shared';
import { ANKETA_TYPE_CARDS } from '../anketaTypeCards';

export { QUOTE_FURNITURE_TYPES as FURNITURE_TYPES };
export type { QuoteFurnitureType };

/**
 * Identity mapping `anketaType → ANKETA_TYPE_CARDS.label`. 8 anketa types,
 * 8 distinct furniture type labels. Используется в `seedQuoteFromAnketa`
 * (Phase D5 Step 8) для derivation initial `state.furnitureType` при первом
 * приходе на Quote screen из Calculator anketa.
 *
 * Result snapshot:
 *   kitchen          → 'Кухня'
 *   cabinet          → 'Шкаф / Гардероб'
 *   bath-tabletop    → 'Санузел: тумба со столешницей'
 *   bath-shelf-above → 'Санузел: шкаф над антресолью'
 *   bath-cabinet     → 'Санузел: шкаф'
 *   tv-zone          → 'ТВ-зона в гостиную'
 *   balcony          → 'Шкаф на балкон'
 *   other            → 'Другая мебель'
 */
export const ANKETA_TO_FURNITURE_TYPE: Record<CalculatorAnketaType, string> = Object.fromEntries(
  ANKETA_TYPE_CARDS.map((card) => [card.anketaType, card.label]),
) as Record<CalculatorAnketaType, string>;
